import { CssBaseline } from "@mui/material";
import { ErrorBoundary } from "@sentry/react";
// import { TanstackDevtools } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";
// import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import {
  createRootRouteWithContext,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
// import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import AppInitializer from "~/AppInitializer";
import { FirstStepsModal } from "~/components/feature/first-steps";
import { PageLoader } from "~/components/ui";
import { selectIsLoadingAuth, selectUser } from "~/store";

export interface RouterAuthContext {
  isAuthenticated: boolean;
  userEmail: string | null;
  isLoadingAuth: boolean;
}

interface RouterContext {
  queryClient: QueryClient;
  authState: RouterAuthContext;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: () => {
    const Initializer = useMemo(() => <AppInitializer />, []);
    const isLoadingAuth = useSelector(selectIsLoadingAuth);
    const user = useSelector(selectUser);

    const [showFirstStepsModal, setShowFirstStepsModal] = useState(false);
    const location = useLocation();

    // Don't show loader on login page with expired status
    const isLoginExpired =
      location.pathname === "/login" && location.search.status === "expired";
    const shouldShowLoader = isLoadingAuth && !isLoginExpired;

    const handleCloseFirstStepsModal = () => setShowFirstStepsModal(false);

    useEffect(() => {
      if (user?.showFirstSteps && !isLoadingAuth) setShowFirstStepsModal(true);
    }, [user?.showFirstSteps, isLoadingAuth]);

    return (
      <ErrorBoundary fallback={<div>An unexpected error occurred.</div>}>
        <>
          {Initializer}
          <CssBaseline />
          <Outlet />
          <PageLoader open={shouldShowLoader} />
          <FirstStepsModal
            open={showFirstStepsModal}
            onClose={handleCloseFirstStepsModal}
          />
          {/* <TanstackDevtools
            config={{
              position: "bottom-right",
            }}
            plugins={[
              {
                name: "Tanstack Router",
                render: <TanStackRouterDevtoolsPanel />, 
              },
              {
                name: "Tanstack Query",
                render: <ReactQueryDevtoolsPanel />, 
              },
            ]}
          /> */}
        </>
      </ErrorBoundary>
    );
  },
  notFoundComponent: () => {
    return <NotFound />;
  },
});

function NotFound() {
  const navigate = useNavigate();

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate is stable from useNavigate
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate({ to: "/dashboard/results/highlights", replace: true });
    }, 0);
    return () => clearTimeout(timer);
  }, []);

  return null;
}
